import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Edit } from "react-feather";
import {
    Button,
    Col,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Input,
    Label,
} from "reactstrap";
import Breadcrumb from "../../common/breadcrumb";
import { useSelector } from "react-redux";
import {
    getProductOptions,
    createProductOption,
    updateProductOption,
    deactivateProductOption,
    getProductAttributes,
    createProductAttribute,
    updateProductAttribute,
    deactivateProductAttribute,
    createAttributeValue,
    updateAttributeValue,
    deactivateAttributeValue,
} from "../../../utils/productAttributesApi";
import { successToast, errorToast } from "../../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../../reporting/ReportingShared";

const TABS = [
    // "attributes" is a special tab: purchase options (Size/Grind) managed as
    // attribute -> values, not SystemProductOptions rows.
    { kind: "attributes", title: "Attributes", isAttributeTab: true },
    { kind: "coffeeKind", title: "Coffee Kinds", hasImage: true, hasLevel: false },
    { kind: "fruitinessLevel", title: "Fruitiness Levels", hasImage: false, hasLevel: true, levelLabel: "Level (position on the slider, 1 = lowest)" },
    { kind: "flavorFamily", title: "Flavour Families", hasImage: true, hasLevel: false },
    { kind: "texture", title: "Textures", hasImage: true, hasLevel: false },
    { kind: "roastLevel", title: "Roast Levels", hasImage: false, hasLevel: true, levelLabel: "Level (roast dots, 1 = lightest, max 5)" },
    { kind: "flavorNote", title: "Flavour Notes", hasImage: false, hasLevel: false },
];

const EMPTY_FORM = { name: "", arabicName: "", level: "", sortOrder: "0", isActive: true };

const tabBtn = (active) => ({
    background: active ? C.coral : C.white,
    color: active ? C.white : C.textMed,
    border: `1.5px solid ${active ? C.coral : C.border}`,
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 13,
    padding: "7px 14px",
    cursor: "pointer",
});

const coralBtn = {
    background: C.coral,
    border: "none",
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 13,
    color: C.white,
    padding: "8px 16px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
};

const dangerBtn = {
    background: C.white,
    border: `1px solid #dc3545`,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    color: "#dc3545",
    padding: "5px 12px",
    cursor: "pointer",
};

const iconBtn = {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.textMed,
    padding: "5px 10px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
};

// devices-list modal conventions
const modalHeaderStyle = { borderBottom: `2px solid ${C.borderLight}` };
const modalBodyStyle = { padding: 24, fontFamily: "'Inter', sans-serif" };
const modalFooterStyle = { borderTop: `1px solid ${C.borderLight}`, gap: 8 };
const modalLabel = { fontWeight: 600, fontSize: 13, color: C.text };
const modalSubmitBtn = { background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 };
const modalCancelBtn = { background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 };

const ProductMasterData = () => {
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [activeKind, setActiveKind] = useState(TABS[0].kind);
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingOption, setEditingOption] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [imageFile, setImageFile] = useState(null);

    // Attributes tab state
    const [attributes, setAttributes] = useState([]);
    const [attrModalOpen, setAttrModalOpen] = useState(false);
    const [editingAttribute, setEditingAttribute] = useState(null);
    const [attrForm, setAttrForm] = useState({ name: "", arabicName: "", sortOrder: "0", isActive: true });
    const [valueRows, setValueRows] = useState([]);

    const activeTab = useMemo(() => TABS.find((t) => t.kind === activeKind), [activeKind]);
    const merchantSelected = selectedMerchant && selectedMerchant !== "all";

    const fetchOptions = useCallback(async () => {
        if (!merchantSelected) {
            setOptions([]);
            setAttributes([]);
            return;
        }
        setIsLoading(true);
        if (activeTab?.isAttributeTab) {
            const data = await getProductAttributes({ merchantId: selectedMerchant, includeInactive: true });
            setAttributes(data || []);
        } else {
            const data = await getProductOptions({ merchantId: selectedMerchant, kind: activeKind });
            setOptions(data || []);
        }
        setIsLoading(false);
    }, [merchantSelected, selectedMerchant, activeKind, activeTab]);

    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    // ---- Option modal handlers ----------------------------------------------

    const openCreateModal = () => {
        setEditingOption(null);
        setForm(EMPTY_FORM);
        setImageFile(null);
        setModalOpen(true);
    };

    const openEditModal = (option) => {
        setEditingOption(option);
        setForm({
            name: option.name || "",
            arabicName: option.arabicName || "",
            level: option.level ?? "",
            sortOrder: String(option.sortOrder ?? 0),
            isActive: option.is_active,
        });
        setImageFile(null);
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            errorToast("Name is required.");
            return;
        }
        if (activeTab.hasLevel && form.level === "") {
            errorToast(`Level is required for ${activeTab.title.toLowerCase()}.`);
            return;
        }

        const payload = {
            name: form.name.trim(),
            arabicName: form.arabicName.trim(),
            level: form.level === "" ? undefined : form.level,
            sortOrder: form.sortOrder === "" ? undefined : form.sortOrder,
            isActive: form.isActive,
        };

        let result;
        if (editingOption) {
            result = await updateProductOption(editingOption.id, payload, imageFile);
        } else {
            result = await createProductOption(
                { ...payload, merchantId: selectedMerchant, kind: activeKind },
                imageFile
            );
        }

        if (result) {
            successToast(editingOption ? "Option updated." : "Option created.");
            setModalOpen(false);
            fetchOptions();
        }
    };

    const handleDeactivate = async (option) => {
        if (!window.confirm(`Deactivate "${option.name}"? It will disappear from the quiz until re-activated.`)) return;
        const result = await deactivateProductOption(option.id);
        if (result) {
            successToast("Option deactivated.");
            fetchOptions();
        }
    };

    // ---- Attribute modal handlers -------------------------------------------

    const openCreateAttrModal = () => {
        setEditingAttribute(null);
        setAttrForm({ name: "", arabicName: "", sortOrder: "0", isActive: true });
        setValueRows([{ name: "", arabicName: "", sortOrder: "0" }]);
        setAttrModalOpen(true);
    };

    const openEditAttrModal = (attribute) => {
        setEditingAttribute(attribute);
        setAttrForm({
            name: attribute.name || "",
            arabicName: attribute.arabicName || "",
            sortOrder: String(attribute.sortOrder ?? 0),
            isActive: attribute.is_active,
        });
        setValueRows(
            (attribute.values || []).map((v) => ({
                id: v.id,
                name: v.name || "",
                arabicName: v.arabicName || "",
                sortOrder: String(v.sortOrder ?? 0),
                is_active: v.is_active,
            }))
        );
        setAttrModalOpen(true);
    };

    const addValueRow = () =>
        setValueRows((rows) => [...rows, { name: "", arabicName: "", sortOrder: String(rows.length) }]);

    const updateValueRow = (index, field, value) =>
        setValueRows((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));

    const removeValueRow = async (index) => {
        const row = valueRows[index];
        if (row.id) {
            if (!window.confirm(`Deactivate value "${row.name}"?`)) return;
            const result = await deactivateAttributeValue(row.id);
            if (!result) return;
            successToast("Value deactivated.");
        }
        setValueRows((rows) => rows.filter((_, i) => i !== index));
    };

    const handleAttrSubmit = async () => {
        if (!attrForm.name.trim()) {
            errorToast("Attribute name is required.");
            return;
        }

        const attrPayload = {
            name: attrForm.name.trim(),
            arabicName: attrForm.arabicName.trim(),
            sortOrder: attrForm.sortOrder,
            isActive: attrForm.isActive,
        };

        let attribute = editingAttribute;
        if (editingAttribute) {
            attribute = await updateProductAttribute(editingAttribute.id, attrPayload);
        } else {
            attribute = await createProductAttribute({ ...attrPayload, merchantId: selectedMerchant });
        }
        if (!attribute) return;

        // Values: rows with id -> update, rows without -> create.
        for (const row of valueRows) {
            if (!row.name.trim()) continue;
            const valuePayload = {
                name: row.name.trim(),
                arabicName: row.arabicName.trim(),
                sortOrder: row.sortOrder,
            };
            if (row.id) {
                await updateAttributeValue(row.id, valuePayload);
            } else {
                await createAttributeValue(attribute.id, valuePayload);
            }
        }

        successToast(editingAttribute ? "Attribute updated." : "Attribute created.");
        setAttrModalOpen(false);
        fetchOptions();
    };

    const handleDeactivateAttribute = async (attribute) => {
        if (!window.confirm(`Deactivate "${attribute.name}"? Products keep their attachments; it disappears from the storefront.`)) return;
        const result = await deactivateProductAttribute(attribute.id);
        if (result) {
            successToast("Attribute deactivated.");
            fetchOptions();
        }
    };

    // ---- Render ---------------------------------------------------------------

    return (
        <Fragment>
            <Breadcrumb title="Product Master Data" parent="Products" />
            <style>{reportingGlobalCSS}</style>
            <div className="rpt-wrap" style={{ padding: "0 24px 24px" }}>
                <RCard
                    title={
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: 10 }}>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {TABS.map((tab) => (
                                    <button key={tab.kind} style={tabBtn(activeKind === tab.kind)} onClick={() => setActiveKind(tab.kind)}>
                                        {tab.title}
                                    </button>
                                ))}
                            </div>
                            <button
                                style={{ ...coralBtn, opacity: merchantSelected ? 1 : 0.5 }}
                                disabled={!merchantSelected}
                                onClick={activeTab.isAttributeTab ? openCreateAttrModal : openCreateModal}
                            >
                                + Add {activeTab.title.replace(/s$/, "")}
                            </button>
                        </div>
                    }
                >
                    {isLoading ? (
                        <Loader />
                    ) : activeTab.isAttributeTab ? (
                        attributes.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "40px", color: C.textMed }}>
                                {!merchantSelected
                                    ? "Please select a merchant to manage attributes."
                                    : 'No attributes yet. Add "Size" or "Grind" to get started.'}
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>ID</th>
                                            <th style={tbl.th}>Name</th>
                                            <th style={tbl.th}>Arabic Name</th>
                                            <th style={tbl.th}>Values</th>
                                            <th style={tbl.th}>Sort</th>
                                            <th style={tbl.th}>Status</th>
                                            <th style={tbl.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {attributes.map((attribute) => (
                                            <tr key={attribute.id} className="rpt-row">
                                                <td style={tbl.td}>{attribute.id}</td>
                                                <td style={{ ...tbl.td, fontWeight: 600 }}>{attribute.name}</td>
                                                <td style={tbl.td} dir="rtl">{attribute.arabicName || "—"}</td>
                                                <td style={{ ...tbl.td, maxWidth: 360 }}>
                                                    {(attribute.values || []).filter((v) => v.is_active).map((v) => (
                                                        <span
                                                            key={v.id}
                                                            style={{
                                                                display: "inline-block",
                                                                padding: "2px 10px",
                                                                margin: 2,
                                                                borderRadius: 12,
                                                                fontSize: 12,
                                                                background: C.coralLight,
                                                                border: `1px solid ${C.coral}`,
                                                                color: C.coralDark,
                                                            }}
                                                        >
                                                            {v.name}
                                                        </span>
                                                    ))}
                                                    {!(attribute.values || []).some((v) => v.is_active) && (
                                                        <span style={{ color: C.textLight }}>No values</span>
                                                    )}
                                                </td>
                                                <td style={tbl.td}>{attribute.sortOrder}</td>
                                                <td style={tbl.td}>
                                                    <Badge
                                                        color={attribute.is_active ? C.green : "#dc3545"}
                                                        bg={attribute.is_active ? C.greenLight : "#fdeaea"}
                                                    >
                                                        {attribute.is_active ? "ACTIVE" : "INACTIVE"}
                                                    </Badge>
                                                </td>
                                                <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>
                                                    <button style={{ ...iconBtn, marginRight: 6 }} onClick={() => openEditAttrModal(attribute)}>
                                                        <Edit style={{ width: 14, height: 14 }} />
                                                    </button>
                                                    {attribute.is_active && (
                                                        <button style={dangerBtn} onClick={() => handleDeactivateAttribute(attribute)}>
                                                            Deactivate
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    ) : options.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "40px", color: C.textMed }}>
                            {!merchantSelected
                                ? "Please select a merchant to manage options."
                                : "No options yet. Add the first one."}
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={tbl.table}>
                                <thead>
                                    <tr>
                                        <th style={tbl.th}>ID</th>
                                        {activeTab.hasImage && <th style={tbl.th}>Icon</th>}
                                        <th style={tbl.th}>Name</th>
                                        <th style={tbl.th}>Arabic Name</th>
                                        {activeTab.hasLevel && <th style={tbl.th}>Level</th>}
                                        <th style={tbl.th}>Sort</th>
                                        <th style={tbl.th}>Status</th>
                                        <th style={tbl.th}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {options.map((option) => (
                                        <tr key={option.id} className="rpt-row">
                                            <td style={tbl.td}>{option.id}</td>
                                            {activeTab.hasImage && (
                                                <td style={tbl.td}>
                                                    {option.image ? (
                                                        <img
                                                            src={`${process.env.REACT_APP_URL}${option.image}`}
                                                            alt={option.name}
                                                            style={{
                                                                width: 36,
                                                                height: 36,
                                                                objectFit: "contain",
                                                                borderRadius: 6,
                                                                background: C.coralLight,
                                                            }}
                                                        />
                                                    ) : (
                                                        <span style={{ color: C.textLight }}>—</span>
                                                    )}
                                                </td>
                                            )}
                                            <td style={{ ...tbl.td, fontWeight: 600 }}>{option.name}</td>
                                            <td style={tbl.td} dir="rtl">{option.arabicName || "—"}</td>
                                            {activeTab.hasLevel && <td style={tbl.td}>{option.level ?? "—"}</td>}
                                            <td style={tbl.td}>{option.sortOrder}</td>
                                            <td style={tbl.td}>
                                                <Badge
                                                    color={option.is_active ? C.green : "#dc3545"}
                                                    bg={option.is_active ? C.greenLight : "#fdeaea"}
                                                >
                                                    {option.is_active ? "ACTIVE" : "INACTIVE"}
                                                </Badge>
                                            </td>
                                            <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>
                                                <button style={{ ...iconBtn, marginRight: 6 }} onClick={() => openEditModal(option)}>
                                                    <Edit style={{ width: 14, height: 14 }} />
                                                </button>
                                                {option.is_active && (
                                                    <button style={dangerBtn} onClick={() => handleDeactivate(option)}>
                                                        Deactivate
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </RCard>

                <Modal isOpen={attrModalOpen} toggle={() => setAttrModalOpen(false)} size="lg" centered>
                    <ModalHeader toggle={() => setAttrModalOpen(false)} style={modalHeaderStyle}>
                        <span style={{ fontWeight: 700, color: C.text }}>{editingAttribute ? "Edit Attribute" : "Add Attribute"}</span>
                    </ModalHeader>
                    <ModalBody style={modalBodyStyle}>
                        <Row>
                            <Col md="5">
                                <FormGroup>
                                    <Label style={modalLabel}>Name * (e.g. Size, Grind)</Label>
                                    <Input
                                        type="text"
                                        value={attrForm.name}
                                        onChange={(e) => setAttrForm({ ...attrForm, name: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label style={modalLabel}>Arabic Name</Label>
                                    <Input
                                        type="text"
                                        dir="rtl"
                                        value={attrForm.arabicName}
                                        onChange={(e) => setAttrForm({ ...attrForm, arabicName: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label style={modalLabel}>Sort Order</Label>
                                    <Input
                                        type="number"
                                        value={attrForm.sortOrder}
                                        onChange={(e) => setAttrForm({ ...attrForm, sortOrder: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        {editingAttribute && (
                            <FormGroup>
                                <Label style={modalLabel}>Status</Label>
                                <Input
                                    type="select"
                                    value={attrForm.isActive ? "true" : "false"}
                                    onChange={(e) => setAttrForm({ ...attrForm, isActive: e.target.value === "true" })}
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </Input>
                            </FormGroup>
                        )}

                        <hr />
                        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: "10px" }}>
                            <Label style={{ ...modalLabel, marginBottom: 0 }}>Values</Label>
                            <Button style={{ ...modalSubmitBtn, padding: "5px 12px", fontSize: 12 }} size="sm" onClick={addValueRow}>
                                + Add Value
                            </Button>
                        </div>
                        {valueRows.map((row, index) => (
                            <Row key={row.id ?? `new-${index}`} className="align-items-center" style={{ marginBottom: "8px" }}>
                                <Col md="4">
                                    <Input
                                        type="text"
                                        placeholder="Value name (e.g. 250g)"
                                        value={row.name}
                                        onChange={(e) => updateValueRow(index, "name", e.target.value)}
                                    />
                                </Col>
                                <Col md="4">
                                    <Input
                                        type="text"
                                        dir="rtl"
                                        placeholder="Arabic name"
                                        value={row.arabicName}
                                        onChange={(e) => updateValueRow(index, "arabicName", e.target.value)}
                                    />
                                </Col>
                                <Col md="2">
                                    <Input
                                        type="number"
                                        placeholder="Sort"
                                        value={row.sortOrder}
                                        onChange={(e) => updateValueRow(index, "sortOrder", e.target.value)}
                                    />
                                </Col>
                                <Col md="2">
                                    <Button
                                        color="btn"
                                        style={{ color: "#dc3545" }}
                                        onClick={() => removeValueRow(index)}
                                    >
                                        Remove
                                    </Button>
                                </Col>
                            </Row>
                        ))}
                        <small style={{ color: "#6b7280" }}>
                            Price differences per value (e.g. 1kg = +37) are set on each product's
                            edit page, because the same value can cost differently per product.
                        </small>
                    </ModalBody>
                    <ModalFooter style={modalFooterStyle}>
                        <Button style={modalCancelBtn} onClick={() => setAttrModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button style={modalSubmitBtn} onClick={handleAttrSubmit} disabled={!attrForm.name.trim()}>
                            {editingAttribute ? "Update" : "Create"}
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
                    <ModalHeader toggle={() => setModalOpen(false)} style={modalHeaderStyle}>
                        <span style={{ fontWeight: 700, color: C.text }}>{editingOption ? `Edit ${activeTab.title.replace(/s$/, "")}` : `Add ${activeTab.title.replace(/s$/, "")}`}</span>
                    </ModalHeader>
                    <ModalBody style={modalBodyStyle}>
                        <FormGroup>
                            <Label style={modalLabel}>Name *</Label>
                            <Input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Espresso"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label style={modalLabel}>Arabic Name</Label>
                            <Input
                                type="text"
                                dir="rtl"
                                value={form.arabicName}
                                onChange={(e) => setForm({ ...form, arabicName: e.target.value })}
                            />
                        </FormGroup>
                        {activeTab.hasLevel && (
                            <FormGroup>
                                <Label style={modalLabel}>{activeTab.levelLabel} *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={form.level}
                                    onChange={(e) => setForm({ ...form, level: e.target.value })}
                                />
                            </FormGroup>
                        )}
                        {activeTab.hasImage && (
                            <FormGroup>
                                <Label style={modalLabel}>Icon (PNG / SVG / WebP)</Label>
                                <Input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                                />
                                {editingOption?.image && !imageFile && (
                                    <small style={{ color: "#6b7280" }}>
                                        Current icon kept unless a new file is chosen.
                                    </small>
                                )}
                            </FormGroup>
                        )}
                        <FormGroup>
                            <Label style={modalLabel}>Sort Order</Label>
                            <Input
                                type="number"
                                value={form.sortOrder}
                                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                            />
                        </FormGroup>
                        {editingOption && (
                            <FormGroup>
                                <Label style={modalLabel}>Status</Label>
                                <Input
                                    type="select"
                                    value={form.isActive ? "true" : "false"}
                                    onChange={(e) => setForm({ ...form, isActive: e.target.value === "true" })}
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </Input>
                            </FormGroup>
                        )}
                    </ModalBody>
                    <ModalFooter style={modalFooterStyle}>
                        <Button style={modalCancelBtn} onClick={() => setModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button style={modalSubmitBtn} onClick={handleSubmit} disabled={!form.name.trim()}>
                            {editingOption ? "Update" : "Create"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        </Fragment>
    );
};

export default ProductMasterData;
